import { KV, SectionH, fmtTime, groupCrewByDept } from "./helpers";

export function CallSheetPreview({ project, callSheet, locations, team }) {
  const locsById = Object.fromEntries(locations.map((l) => [l.id, l]));
  const teamById = Object.fromEntries(team.map((m) => [m.id, m]));
  const locIds = callSheet.is_archived
    ? callSheet.location_ids || []
    : project?.location_ids?.length
      ? project.location_ids
      : project?.location_id
        ? [project.location_id]
        : [];
  const selectedLocs = locIds
    .map((id) => locsById[id])
    .filter(Boolean);
  const talent = (callSheet.talent || []).map((t) => ({
    ...t,
    person: teamById[t.team_id],
  })).filter((t) => t.person);
  const crew = (callSheet.crew || []).map((c) => ({
    ...c,
    person: teamById[c.team_id],
  })).filter((c) => c.person);
  const clients = (callSheet.clients || []).map((c) => ({
    ...c,
    person: teamById[c.team_id],
  })).filter((c) => c.person);

  const hospitals = [
    ...new Set(
      selectedLocs
        .map((l) => (l.nearest_hospital || "").trim())
        .filter(Boolean)
    ),
  ];

  return (
    <div className="bg-white text-zinc-900 rounded-md overflow-hidden shadow-2xl">
      <div className="bg-zinc-950 px-4 py-3 flex items-center justify-between">
        <img
          src="https://customer-assets.emergentagent.com/job_shoot-organizer-1/artifacts/3jmotfdo_WC%20Logo%202018%20FULL%20Outlined%20WHITE.png"
          alt="Whiplash"
          className="h-6"
        />
        <span className="text-amber-500 text-[9px] font-semibold tracking-[0.25em] uppercase">
          Call Sheet
        </span>
      </div>
      <div className="p-4 text-xs">
        <div className="border-b-2 border-zinc-900 pb-2 mb-3">
          <div className="font-heading text-lg font-light leading-tight text-zinc-900">
            {project.name}
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {project.job_number && (
              <span
                data-testid="preview-job-number"
                className="inline-block text-[9px] font-mono font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900 border border-amber-300"
              >
                Job #{project.job_number}
              </span>
            )}
            {callSheet.day_of && (
              <div className="text-[9px] uppercase tracking-widest text-zinc-500">
                {callSheet.day_of}
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 mb-3">
          <KV k="Date" v={callSheet.shoot_date || project.shoot_date} />
          <KV k="General Call" v={fmtTime(callSheet.general_call)} amber />
          <KV k="Shoot Call" v={fmtTime(callSheet.shooting_call)} />
          <KV k="Est. Wrap" v={fmtTime(callSheet.est_wrap)} />
          <KV k="Weather" v={callSheet.weather} />
          <KV k="Sunrise / Sunset" v={[callSheet.sunrise, callSheet.sunset].filter(Boolean).join(" / ")} />
        </div>
        {selectedLocs.length > 0 && (
          <>
            <SectionH>Locations</SectionH>
            {selectedLocs.map((loc) => (
              <div
                key={loc.id}
                className="border border-zinc-200 border-l-2 border-l-amber-500 rounded-sm p-2 mb-2"
              >
                <div className="font-semibold text-zinc-900">{loc.name}</div>
                {loc.address && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-[10px] text-zinc-700 hover:text-amber-700 mt-0.5"
                    data-testid={`preview-location-address-${loc.id}`}
                  >
                    📍 {loc.address}
                  </a>
                )}
                {loc.parking_info && (
                  <div className="text-[10px] text-zinc-600">🅿 {loc.parking_info}</div>
                )}
              </div>
            ))}
          </>
        )}
        {hospitals.length > 0 && (
          <div data-testid="preview-hospital-section">
            <div
              className="text-[9px] uppercase tracking-[0.16em] font-semibold pb-1 mb-1.5 border-b"
              style={{ color: "#b91c1c", borderColor: "#b91c1c" }}
            >
              Nearest Hospital · Emergency: 911
            </div>
            <div className="bg-rose-50 border-l-2 border-rose-600 rounded-sm p-2 mb-2 space-y-1">
              {hospitals.map((h, i) => (
                <a
                  key={i}
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(h)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-[10px] text-rose-900 hover:text-rose-700 font-semibold"
                  data-testid={`preview-hospital-${i}`}
                >
                  🏥 {h}
                </a>
              ))}
            </div>
          </div>
        )}
        {talent.length > 0 && (
          <>
            <SectionH>Talent</SectionH>
            <table className="w-full text-[10px] mb-2">
              <thead>
                <tr className="border-b border-zinc-900 text-zinc-500 uppercase text-[8px] tracking-wider">
                  <th className="text-left py-1">Name</th>
                  <th className="text-left">Role</th>
                  <th className="text-left">Call</th>
                </tr>
              </thead>
              <tbody>
                {talent.map((t) => (
                  <tr key={t.team_id} className="border-b border-zinc-100">
                    <td className="py-1">{t.person.name}</td>
                    <td>{t.person.role}</td>
                    <td className="text-amber-700 font-semibold">{fmtTime(t.call_time) || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {crew.length > 0 && (
          <>
            <SectionH>Crew</SectionH>
            {groupCrewByDept(crew).map(([dept, items]) => (
              <div key={dept} className="mb-2">
                <div className="text-[8px] uppercase tracking-[0.18em] font-bold text-zinc-900 border-b border-zinc-300 pb-0.5 mb-1">
                  {dept}
                </div>
                <table className="w-full text-[10px]">
                  <tbody>
                    {items.map((c) => (
                      <tr key={c.team_id}>
                        <td className="py-0.5">{c.person.name}</td>
                        <td className="text-zinc-500">{c.person.role}</td>
                        <td className="text-right text-amber-700 font-semibold">
                          {fmtTime(c.call_time) || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </>
        )}
        {clients.length > 0 && (
          <>
            <SectionH>Client</SectionH>
            <table className="w-full text-[10px] mb-2" data-testid="preview-client-table">
              <thead>
                <tr className="border-b border-zinc-900 text-zinc-500 uppercase text-[8px] tracking-wider">
                  <th className="text-left py-1">Name</th>
                  <th className="text-left">Role</th>
                  <th className="text-left">Company</th>
                  <th className="text-right">Call</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.team_id} className="border-b border-zinc-100">
                    <td className="py-1">{c.person.name}</td>
                    <td>{c.person.role}</td>
                    <td className="text-sky-700">{c.person.company || "-"}</td>
                    <td className="text-right text-amber-700 font-semibold">
                      {fmtTime(c.call_time) || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {callSheet.production_notes && (
          <>
            <SectionH>Notes</SectionH>
            <div className="text-[10px] bg-zinc-50 border-l-2 border-amber-500 p-2 whitespace-pre-line">
              {callSheet.production_notes}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
